/**
 * Calendar Synchronization Utilities
 * 
 * This file contains utility functions to enhance synchronization between
 * the admin calendar and landing page calendar components.
 */

import { toast } from 'react-hot-toast';

/**
 * Force refresh of calendar data by clearing localStorage cache
 * This helps ensure both admin and landing page calendars stay in sync
 */
export const forceClearCalendarCache = () => {
  try {
    // Clear any localStorage cache keys related to calendar data
    const keysToRemove: string[] = [];
    
    // Find all calendar-related localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('calendar-') || 
        key.startsWith('cal-data-') || 
        key.includes('calendar') ||
        key.includes('availability')
      )) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all identified keys
    keysToRemove.forEach(key => {
      console.log(`Clearing calendar cache key: ${key}`);
      localStorage.removeItem(key);
    });
    
    console.log(`Cleared ${keysToRemove.length} calendar cache entries`);
    return true;
  } catch (error) {
    console.error('Error clearing calendar cache:', error);
    return false;
  }
};

/**
 * Broadcast a calendar update event to all open tabs/windows
 * This helps ensure real-time synchronization across multiple browser tabs
 */
export const broadcastCalendarUpdate = (eventType: string, data?: any) => {
  try {
    // Use BroadcastChannel API for cross-tab communication
    const channel = new BroadcastChannel('calendar-sync-channel');
    
    // Send the update event
    channel.postMessage({
      type: 'calendar-update',
      eventType,
      data,
      timestamp: new Date().toISOString()
    });
    
    // Close the channel
    channel.close();
    
    console.log(`Broadcast calendar update: ${eventType}`);
    return true;
  } catch (error) {
    console.error('Error broadcasting calendar update:', error);
    return false;
  }
};

/**
 * Listen for calendar update events from other tabs/windows
 * @param callback Function to call when an update is received
 * @returns Cleanup function to remove the listener
 */
export const listenForCalendarUpdates = (callback: (eventType: string, data?: any) => void) => {
  try {
    // Create a channel for receiving updates
    const channel = new BroadcastChannel('calendar-sync-channel');
    
    // Set up the message handler
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'calendar-update') {
        console.log('Received calendar update:', event.data);
        callback(event.data.eventType, event.data.data);
        
        // Show a toast notification
        toast.success('Calendar updated with latest availability');
      }
    };
    
    // Add the event listener
    channel.addEventListener('message', handleMessage);
    
    // Return a cleanup function
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  } catch (error) {
    console.error('Error setting up calendar update listener:', error);
    return () => {}; // Return empty cleanup function
  }
};
